import { useCallback, useContext, useState } from "react";
import { ApolloError } from "@apollo/client";
import { GraphQLError } from "graphql";
import {
  CartErrorMsg,
  CheckoutSessionErrorExtensions,
  LocalizationCheckoutErrors,
  UniversalCheckoutErrorKeys,
} from "v3/types/error";
import { LocalizationContext } from "v3/context/localization";
import { ItemErrorMsg, ItemWithErrorMsgs } from "@pepdirect/v3/types";
import {
  parseAndLogUnknownErrors,
  parseApplyDiscountGraphQLErrors,
  parseApplySavedShippingAddressGraphQLErrors,
  parseFinalizePriceGraphQLErrors,
  parseRemoveDiscountGraphQLErrors,
  parseRemoveGiftInfoGraphQLErrors,
  parseRetrieveAddressGraphQLErrors,
  parseSaveEnteredContactInfoGraphQLErrors,
  parseSaveGiftInfoGraphQLErrors,
  parseSavePaymentGraphQLErrors,
  parseSavePaymentOptionsGraphQLErrors,
  parseSaveShippingOptionsGraphQLErrors,
  parseSaveSuggestedAddressGraphQLErrors,
  parseShopToCheckoutAndSubmitOrderGraphQLErrors,
} from "v3/helpers/errors";
import { useCurrentAppId } from "v3/hooks/useCurrentAppId";
import { useRouter } from "next/router";

interface GenericErrorHandlingMap {
  [key: string]: (
    graphQLErrors: GraphQLError[],
    localization?: LocalizationCheckoutErrors
  ) => { errorMsg: string };
}

const discountErrorHandlingMap: GenericErrorHandlingMap = {
  applyDiscount: parseApplyDiscountGraphQLErrors,
  removeDiscount: parseRemoveDiscountGraphQLErrors,
};

const genericErrorHandlingMap: GenericErrorHandlingMap = {
  applySavedShippingAddress: parseApplySavedShippingAddressGraphQLErrors,
  finalizePrice: parseFinalizePriceGraphQLErrors,
  removeGiftInfo: parseRemoveGiftInfoGraphQLErrors,
  retrieveAddress: parseRetrieveAddressGraphQLErrors,
  saveEnteredContactInfo: parseSaveEnteredContactInfoGraphQLErrors,
  saveGiftInfo: parseSaveGiftInfoGraphQLErrors,
  savePayment: parseSavePaymentGraphQLErrors,
  savePaymentOptions: parseSavePaymentOptionsGraphQLErrors,
  saveShippingOptions: parseSaveShippingOptionsGraphQLErrors,
  saveSuggestedAddress: parseSaveSuggestedAddressGraphQLErrors,
};

export interface CheckoutErrorContextInterface {
  cartErrors: CartErrorMsg[];
  clearAllErrors: () => void;
  discountErrorMsg: string;
  errorMsg: string;
  handleCheckoutErrors: (e: unknown) => void;
  handleUniversalErrors: (graphQLErrors: GraphQLError[]) => boolean;
  itemErrors: ItemWithErrorMsgs[];
  loyaltyError: ItemErrorMsg | null;
  setDiscountErrorMsg: (prop: string) => void;
  setErrorMsg: (prop: string) => void;
  setLoyaltyError: (error: ItemErrorMsg | null) => void;
  show404Page: boolean;
  showPaymentProcessingPage: boolean;
  showEmptyCartPage: boolean;
  showLoyaltyErrorPage: boolean;
  setShowLoyaltyErrorPage: (show: boolean) => void;
  giftCardError: ItemErrorMsg | null;
  setGiftCardError: (error: ItemErrorMsg | null) => void;
}

export function useCheckoutErrorProvider(): {
  checkoutErrorContextValue: CheckoutErrorContextInterface;
} {
  const { genericErrorMsg, localization } = useContext(LocalizationContext);
  const { appId } = useCurrentAppId();
  const router = useRouter();

  const {
    discountCannotApplyToSubscription = "Sorry, this discount cannot be combined with a subscription discount.",
    discountHeadingText = "Adjust discount to continue",
    discountNoteText = "Your current points discount exceeds the order total. Please go back and select a smaller discount using fewer points to proceed with checkout.",
    discountTooLarge = "Order total too low to apply coupon code.",
    discountExceedsSubtotal = "Sorry, your subtotal is too low for this promo code.",
    discountPromoCodeRequiresEmailOrLogin = "Sorry, to redeem this coupon code, please log in or enter your email address as a guest.",
    discountNotFound = "Items in your cart may be excluded, have a discount, or this code may be invalid.",
  } = localization?.checkout?.errors?.discount || {};

  const [cartErrorsState, setCartErrorsState] = useState<CartErrorMsg[]>([]);
  const [itemErrorsState, setItemErrorsState] = useState<ItemWithErrorMsgs[]>(
    []
  );
  const [show404Page, setShow404Page] = useState(false);
  const [showPaymentProcessingPage, setShowPaymentProcessingPage] =
    useState(false);
  const [showEmptyCartPage, setShowEmptyCartPage] = useState(false);
  const [showLoyaltyErrorPage, setShowLoyaltyErrorPage] = useState(false);
  const [discountErrorMsg, setDiscountErrorMsg] = useState("");
  const [loyaltyError, setLoyaltyError] = useState<ItemErrorMsg | null>(null);
  const [giftCardError, setGiftCardError] = useState<ItemErrorMsg | null>(null);
  const [errorMsgState, setErrorMsgState] = useState("");

  const clearAllErrors = useCallback(() => {
    setShow404Page(false);
    setShowEmptyCartPage(false);
    setShowLoyaltyErrorPage(false);
    setErrorMsgState("");
    setCartErrorsState([]);
    setItemErrorsState([]);
    setDiscountErrorMsg("");
    setLoyaltyError(null);
  }, []);

  const handleUniversalErrors = useCallback(
    (graphQLErrors: GraphQLError[]) => {
      let hasUniversalError = false;
      const operationName = graphQLErrors[0].message;

      graphQLErrors.forEach((error: GraphQLError) => {
        const errorDetails =
          (error.extensions as CheckoutSessionErrorExtensions<string>)
            ?.details || [];

        // non-array errorDetails will get logged to Sentry in errorLink
        if (!Array.isArray(errorDetails)) return;
        errorDetails.forEach((errorDetail) => {
          const { key, details } = errorDetail;
          const { orderId } = details || {};
          const href = `/v3/status/${orderId}/${appId}`;
          switch (key) {
            // show 404 page
            case UniversalCheckoutErrorKeys.cartNotFound:
            case UniversalCheckoutErrorKeys.sessionNotFound:
              setShow404Page(true);
              hasUniversalError = true;
              break;
            case UniversalCheckoutErrorKeys.paymentProcessing:
              setShowPaymentProcessingPage(true);
              hasUniversalError = true;
              break;
            case UniversalCheckoutErrorKeys.orderProcessed:
              router.push(href);
              hasUniversalError = true;
              break;
            // show empty cart page
            case UniversalCheckoutErrorKeys.emptyCart:
              setShowEmptyCartPage(true);
              hasUniversalError = true;
              break;
            // show discount errors
            case UniversalCheckoutErrorKeys.discountCannotApplyToSubscription:
              setDiscountErrorMsg(discountCannotApplyToSubscription);
              hasUniversalError = true;
              break;
            case UniversalCheckoutErrorKeys.discountExceedsSubtotal:
              setDiscountErrorMsg(discountExceedsSubtotal);
              hasUniversalError = true;
              break;
            case UniversalCheckoutErrorKeys.discountPromoCodeRequiresEmail:
              setDiscountErrorMsg(discountPromoCodeRequiresEmailOrLogin);
              hasUniversalError = true;
              break;
            case UniversalCheckoutErrorKeys.discountPromoCodeNotFound:
            case UniversalCheckoutErrorKeys.discountPromoCodeNotApplicable:
            case UniversalCheckoutErrorKeys.discountLimitExceeded:
              setDiscountErrorMsg(discountNotFound);
              hasUniversalError = true;
              break;
            // special loyalty error handling
            case UniversalCheckoutErrorKeys.loyaltyDiscountTooLarge:
              if (operationName === "shopToCheckout") {
                setShowLoyaltyErrorPage(true);
              } else if (operationName === "applyDiscount") {
                setDiscountErrorMsg(discountTooLarge);
              } else {
                setLoyaltyError({
                  alertType: "error",
                  title: discountHeadingText,
                  note: discountNoteText,
                });
              }
              hasUniversalError = true;
              break;
            // show generic cart error
            // shipping error when needed for billing
            case UniversalCheckoutErrorKeys.incompleteShippingAddress:
            case UniversalCheckoutErrorKeys.failedToCalculateTaxes:
              setErrorMsgState(genericErrorMsg);
              hasUniversalError = true;
              break;
            default:
            // no default
          }
        });
      });

      return hasUniversalError;
    },
    [
      discountCannotApplyToSubscription,
      discountExceedsSubtotal,
      discountHeadingText,
      discountNoteText,
      discountNotFound,
      discountPromoCodeRequiresEmailOrLogin,
      discountTooLarge,
      genericErrorMsg,
      appId,
      router,
    ]
  );

  const handleCheckoutErrors = useCallback(
    (e: unknown) => {
      // first clear all existing errors
      clearAllErrors();

      if (e instanceof ApolloError && e.graphQLErrors.length) {
        const { graphQLErrors } = e;
        const operationName = graphQLErrors[0].message;

        // handle universal errors first
        const hasUniversalErrors = handleUniversalErrors(
          graphQLErrors as GraphQLError[]
        );
        if (hasUniversalErrors) return;

        // custom error handling
        if (["shopToCheckout", "submitOrder"].includes(operationName)) {
          const {
            cartErrors,
            errorMsg,
            itemErrors,
            loyaltyError,
            giftCardError,
          } = parseShopToCheckoutAndSubmitOrderGraphQLErrors(
            graphQLErrors as GraphQLError[],
            localization?.checkout?.errors
          );

          if (errorMsg) {
            setErrorMsgState(errorMsg);
          } else if (
            cartErrors?.length ||
            itemErrors?.length ||
            loyaltyError ||
            giftCardError
          ) {
            cartErrors?.length && setCartErrorsState(cartErrors);
            itemErrors?.length && setItemErrorsState(itemErrors);
            loyaltyError && setLoyaltyError(loyaltyError);
            giftCardError && setGiftCardError(giftCardError);
          } else {
            setErrorMsgState(genericErrorMsg);
          }
          return;
        }

        // discount error handling
        if (discountErrorHandlingMap[operationName]) {
          const parseFunc = discountErrorHandlingMap[operationName];
          const { errorMsg: parsedErrorMsg } = parseFunc(
            e.graphQLErrors as GraphQLError[],
            localization?.checkout?.errors
          );
          if (parsedErrorMsg) setDiscountErrorMsg(parsedErrorMsg);
          return;
        }

        // generic page-level error handling
        if (genericErrorHandlingMap[operationName]) {
          const parseFunc = genericErrorHandlingMap[operationName];
          const { errorMsg: parsedErrorMsg } = parseFunc(
            e.graphQLErrors as GraphQLError[],
            localization?.checkout?.errors
          );
          setErrorMsgState(parsedErrorMsg || genericErrorMsg);
          return;
        }

        // everything else has no specific handlers defined on the FE
        // so we log them as unknown
        parseAndLogUnknownErrors(e, operationName);
        setErrorMsgState(genericErrorMsg);
      } else {
        setErrorMsgState(genericErrorMsg);
        console.warn(e);
      }
    },
    [
      clearAllErrors,
      handleUniversalErrors,
      localization?.checkout?.errors,
      genericErrorMsg,
    ]
  );

  const checkoutErrorContextValue: CheckoutErrorContextInterface = {
    cartErrors: cartErrorsState,
    clearAllErrors,
    discountErrorMsg,
    errorMsg: errorMsgState,
    handleCheckoutErrors,
    handleUniversalErrors,
    itemErrors: itemErrorsState,
    loyaltyError,
    setDiscountErrorMsg,
    setErrorMsg: setErrorMsgState,
    setLoyaltyError,
    setShowLoyaltyErrorPage,
    show404Page,
    showPaymentProcessingPage,
    showEmptyCartPage,
    showLoyaltyErrorPage,
    giftCardError,
    setGiftCardError,
  };
  return { checkoutErrorContextValue };
}
